import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FaGithub, FaLinkedinIn, FaTwitter, FaInstagram, FaArrowRight } from 'react-icons/fa';
import { HiOutlineDocumentDownload } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import './Home.css';

const Home = () => {
  const navigate = useNavigate();
  const [loopNum, setLoopNum] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [text, setText] = useState('');
  const [delta, setDelta] = useState(300 - Math.random() * 100);
  const period = 2000;

  const roles = [
    "Machine Learning Engineer",
    "Full Stack Developer"
  ];

  useEffect(() => {
    let ticker = setInterval(() => {
      tick();
    }, delta);

    return () => clearInterval(ticker);
  }, [text]);

  const tick = () => {
    let i = loopNum % roles.length;
    let fullText = roles[i];
    let updatedText = isDeleting 
      ? fullText.substring(0, text.length - 1) 
      : fullText.substring(0, text.length + 1);

    setText(updatedText);

    if (isDeleting) {
      setDelta(prevDelta => prevDelta / 2);
    }

    if (!isDeleting && updatedText === fullText) {
      setIsDeleting(true);
      setDelta(period);
    } else if (isDeleting && updatedText === '') {
      setIsDeleting(false);
      setLoopNum(loopNum + 1);
      setDelta(500);
    }
  };

  const handleProjectClick = () => {
    navigate('/projects');
  };

  const handleContactClick = () => {
    navigate('/contact');
  };

  const handleDownloadCV = () => {
    const resumeUrl = 'https://drive.google.com/file/d/1NiKZTQX-gm8bVaqYICl4rv8-OOCb9sZ1/preview';
    window.open(resumeUrl, '_blank');
  };

  return (
    <div className="home-container">
      {/* Animated Background */}
      <div className="animated-background">
        {[...Array(50)].map((_, index) => (
          <div 
            key={`particle-${index}`}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${5 + Math.random() * 10}s`
            }}
          />
        ))}
        {[...Array(100)].map((_, index) => (
          <div
            key={`snow-${index}`}
            className="snow"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 15}s`,
              animationDuration: `${10 + Math.random() * 20}s`
            }}
          />
        ))}
        {[...Array(20)].map((_, index) => (
          <div
            key={`orb-${index}`}
            className="glowing-orb"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: `${15 + Math.random() * 15}s`
            }}
          />
        ))}
      </div>

      {/* Main Content */}
      <motion.div 
        className="content-wrapper"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <motion.h2 
          className="greeting"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          Welcome to my Portfolio
        </motion.h2>

        <motion.h1 
          className="name"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          Hi, I'm <span className="highlight">Rahul Mishra</span>
        </motion.h1>

        <motion.h3 
          className="title"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          I'm a <span className="typed-text">{text}</span>
        </motion.h3>

        <motion.p 
          className="description"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          Passionate about creating beautiful and functional web applications.
          <br />
          Turning ideas into reality through code.
        </motion.p>

        <motion.div 
          className="cta-buttons"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
        >
          <motion.button 
            className="primary-btn"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleProjectClick}
          >
            View Projects <FaArrowRight className="btn-icon" />
          </motion.button>

          <motion.button 
            className="secondary-btn"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleContactClick}
          >
            Contact Me
          </motion.button>

          <motion.button 
            className="download-btn"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleDownloadCV}
          >
            Download CV <HiOutlineDocumentDownload className="btn-icon" />
          </motion.button>
        </motion.div>

        <motion.div 
          className="social-links"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
        >
          <motion.a 
            href="https://github.com/machinelearningprodigy" 
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ y: -5, color: '#4ecdc4' }}
          >
            <FaGithub />
          </motion.a>
          <motion.a 
            href="https://www.linkedin.com/in/rahul-mishra-9a8826269/" 
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ y: -5, color: '#4ecdc4' }}
          >
            <FaLinkedinIn />
          </motion.a>
          <motion.a 
            href="https://x.com/NitishM66803568" 
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ y: -5, color: '#4ecdc4' }}
          >
            <FaTwitter />
          </motion.a>

          <motion.a 
            href="https://www.instagram.com/rahul9076mishra/" 
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ y: -5, color: '#4ecdc4' }}
          >
            <FaInstagram />
          </motion.a>

        </motion.div>
      </motion.div>
    </div>
  );
};

export default Home;